'use client';

import styles from './page.module.css';
import {
    getAddress,
    fetchAddressDetails,
    initTransport,
    fetchAddressBalance,
    isLedgerTransportInitialized,
} from '../../lib/ledger';
import { useState, useEffect } from 'react';
import {
    Stack,
    Tabs,
    Breadcrumbs,
    Anchor,
    Button,
    Center,
    Modal,
    Text,
    Select,
    Loader,
    Group,
} from '@mantine/core';
import Header from '../../components/header';
import AddressesTab from './addresses-tab';
import OverviewTab from './overview-tab';
import TransactionsTab from './transactions-tab';
import { LockedDeviceError } from '@ledgerhq/errors';
import { IconPlus } from '@tabler/icons-react';
import sha256 from 'crypto-js/sha256';

import KaspaBIP32 from '../../lib/bip32';
import { delay } from '../../lib/util';

import { useElementSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import SettingsStore from '../../lib/settings-store';
import { kasToSompi, sompiToKas, NETWORK_UTXO_LIMIT } from '../../lib/kaspa-util';
import { IMempoolEntry } from '../../lib/kaspa-rpc/kaspa';
import { IAddressData, ISelectedAddress } from './types';

const addressLoading = new Map<number, boolean>();
const addressInitialized = new Map<number, boolean>();

const addressFilter = (lastReceiveIndex, lastChangeIndex) => {
    return (addressData, index) => {
        return (
            index == 0 || // Always show the first address
            (addressData.addressType === 0 && addressData.addressIndex <= lastReceiveIndex) || // Always show generated receive addresses
            (addressData.addressType === 1 && addressData.addressIndex <= lastChangeIndex) || // Always show generated change addresses
            addressData.balance > 0
        ); // Always show if balance is positive
    };
};

function loadAddressDetails(rawAddress) {
    const fetchAddressPromise = fetchAddressDetails(rawAddress.address, rawAddress.derivationPath);

    return fetchAddressPromise.then((addressDetails) => {
        rawAddress.balance = sompiToKas(Number(addressDetails.balance));
        rawAddress.utxos = addressDetails.utxos;
        // rawAddress.txCount = addressDetails.txCount;
        rawAddress.loading = false;

        return rawAddress;
    });
}

async function scanAddressType(bip32, addressType, startIndex, account) {
    let nonEmptyAddressFound = true;
    let scanIndexStart = startIndex;
    let latestWithFunds = -1;
    let addressesWithBalancesFound = 0;
    const typeLabel = addressType === 0 ? 'receive' : 'change';

    while (nonEmptyAddressFound) {
        if (scanIndexStart > startIndex) {
            await delay(1000);
        }
        nonEmptyAddressFound = false;

        const batchSize = 5;
        console.info(
            `Scanning ${typeLabel} address range ${scanIndexStart} - ${
                scanIndexStart + batchSize - 1
            }`,
        );

        let promises = [];
        for (
            let addressIndex = scanIndexStart;
            addressIndex < scanIndexStart + batchSize;
            addressIndex++
        ) {
            const address = bip32.getAddress(addressType, addressIndex, account);

            promises.push(
                new Promise(async (resolve, reject) => {
                    try {
                        const balanceData = await fetchAddressBalance(address);
                        resolve({ balanceData, addressIndex });
                    } catch (e) {
                        reject(e);
                    }
                }),
            );
        }

        try {
            const scanResults = await Promise.all(promises);

            for (const result of scanResults) {
                if (result.balanceData.balance > 0) {
                    if (result.addressIndex > latestWithFunds) {
                        latestWithFunds = result.addressIndex;
                    }
                    nonEmptyAddressFound = true;
                    addressesWithBalancesFound++;
                }
            }
        } catch (e) {
            throw e;
        }

        scanIndexStart += 5;
    }

    return { latestWithFunds, addressesWithBalancesFound };
}

async function loadAddressesForType(
    bip32,
    addressType,
    lastIndex,
    rawAddresses,
    callback,
    callbackSetRawAddresses,
    lastReceiveIndex,
    lastChangeIndex,
    account,
) {
    if (lastIndex < 0) {
        return;
    }

    for (let addressIndex = 0; addressIndex <= lastIndex; addressIndex++) {
        const derivationPath = `44'/111111'/${account}'/${addressType}/${addressIndex}`;
        const address = bip32.getAddress(addressType, addressIndex);
        const addressData: IAddressData = {
            key: address,
            address,
            derivationPath,
            balance: 0,
            loading: true,
            addressIndex,
            addressType,
            utxos: [],
        };

        rawAddresses.push(addressData);
        callbackSetRawAddresses(rawAddresses);
        callback(rawAddresses.filter(addressFilter(lastReceiveIndex, lastChangeIndex)));
    }

    let promises = [];
    for (const rawAddress of rawAddresses) {
        if (!rawAddress.loading) {
            continue;
        }

        promises.push(
            loadAddressDetails(rawAddress).then((data) => {
                callback(rawAddresses.filter(addressFilter(lastReceiveIndex, lastChangeIndex)));
                return data;
            }),
        );

        if (promises.length >= 5) {
            await Promise.all(promises);
            promises = [];
        }
    }

    if (promises.length > 0) {
        await Promise.all(promises);
    }
}

async function loadOrScanAddressBatch(
    bip32,
    callback,
    callbackSetRawAddresses,
    userSettings,
    account,
) {
    if (addressLoading.get(account) || addressInitialized.get(account)) {
        return;
    }

    let lastReceiveIndex = userSettings.getSetting('lastReceiveIndex');
    let lastChangeIndex = userSettings.getSetting('lastChangeIndex');

    addressLoading.set(account, true);

    try {
        let rawAddresses: IAddressData[] = [];

        let notifId: string | undefined;
        let totalAddressesFound = 0;

        // Scan receive addresses (type 0)
        if (lastReceiveIndex < 0) {
            notifId = notifications.show({
                title: 'First-time load detected',
                message: 'Scanning for addresses with balance',
                loading: true,
            });
            console.info('Initial load detected. Scanning for addresses');

            try {
                const result = await scanAddressType(bip32, 0, 0, account);
                lastReceiveIndex = result.latestWithFunds >= 0 ? result.latestWithFunds : 0;
                totalAddressesFound += result.addressesWithBalancesFound;
                userSettings.setSetting('lastReceiveIndex', lastReceiveIndex);
                if (result.addressesWithBalancesFound > 0) {
                    userSettings.setSetting('hasTransactions', true);
                }
                console.info(
                    'Receive address scan complete. Last index with funds:',
                    lastReceiveIndex,
                );
            } catch (e) {
                notifications.hide(notifId);
                notifications.show({
                    title: 'Error',
                    message:
                        'Failed to scan for addresses with balance. Refresh the page to retry.',
                    autoClose: false,
                    color: 'red',
                });
                throw e;
            }
        }

        // Scan change addresses (type 1)
        if (lastChangeIndex < 0) {
            try {
                const result = await scanAddressType(bip32, 1, 0, account);
                lastChangeIndex = result.latestWithFunds >= 0 ? result.latestWithFunds : -1;
                totalAddressesFound += result.addressesWithBalancesFound;
                userSettings.setSetting('lastChangeIndex', lastChangeIndex);
                if (result.addressesWithBalancesFound > 0) {
                    userSettings.setSetting('hasTransactions', true);
                }
                console.info(
                    'Change address scan complete. Last index with funds:',
                    lastChangeIndex,
                );
            } catch (e) {
                if (notifId) {
                    notifications.hide(notifId);
                }
                notifications.show({
                    title: 'Error',
                    message:
                        'Failed to scan for change addresses with balance. Refresh the page to retry.',
                    autoClose: false,
                    color: 'red',
                });
                throw e;
            }
        }

        if (notifId) {
            notifications.hide(notifId);
        }
        if (totalAddressesFound > 0) {
            notifications.show({
                title: 'Initial scan complete',
                message: `${totalAddressesFound} ${
                    totalAddressesFound <= 1 ? 'address' : 'addresses'
                } with balance found`,
            });
        }

        // Load receive addresses
        await loadAddressesForType(
            bip32,
            0,
            lastReceiveIndex,
            rawAddresses,
            callback,
            callbackSetRawAddresses,
            lastReceiveIndex,
            lastChangeIndex,
            account,
        );

        // Load change addresses
        await loadAddressesForType(
            bip32,
            1,
            lastChangeIndex,
            rawAddresses,
            callback,
            callbackSetRawAddresses,
            lastReceiveIndex,
            lastChangeIndex,
            account,
        );
    } finally {
        addressInitialized.set(account, true);
        addressLoading.set(account, false);
    }
}

async function demoLoadAddress(bip32, setAddresses, setRawAddresses, lastReceiveIndex) {
    const demoAddresses = [];

    for (let i = 0; i <= lastReceiveIndex; i++) {
        const balance = Math.round(Math.random() * 10000);
        const currAddress = {
            key: i,
            address: bip32.getAddress(0, i),
            balance,
            derivationPath: `44'/111111'/0'/0/${i}`,
            utxos: [],
            loading: true,
        };

        currAddress.utxos.push({
            prevTxId: 'FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF',
            outpointIndex: 0,
            amount: kasToSompi(balance - (NETWORK_UTXO_LIMIT - 1)),
        });

        for (let j = 0; j < NETWORK_UTXO_LIMIT - 1; j++) {
            currAddress.utxos.push({
                prevTxId: 'FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF',
                outpointIndex: 0,
                amount: kasToSompi(1),
            });
        }

        demoAddresses.push(currAddress);

        setRawAddresses([...demoAddresses]);
        setAddresses([...demoAddresses]);

        delay(Math.round(Math.random() * 3000)).then(() => {
            currAddress.loading = false;

            delay(Math.round(Math.random() * 2000)).then(() => {
                setAddresses([...demoAddresses]);
            });
        });
    }
}
async function getXPubFromLedger(accountPath) {
    const { chainCode, compressedPublicKey } = await getAddress(accountPath);

    return { chainCode, compressedPublicKey };
}

async function discoverAccounts() {
    const accounts = new Map<number, KaspaBIP32>();
    const usedAccounts = new Set<number>();
    let accountIndex = 0;

    while (true) {
        const accountPath = `44'/111111'/${accountIndex}'`;
        const xpub = await getXPubFromLedger(accountPath);
        const bip32 = new KaspaBIP32(xpub.compressedPublicKey, xpub.chainCode);

        // Check if this account has been used by scanning first 2 receive addresses
        let hasUsage = false;
        for (let i = 0; i < 2; i++) {
            const addr = bip32.getAddress(0, i);
            const balanceData = await fetchAddressBalance(addr);
            if (Number(balanceData.balance) > 0) {
                hasUsage = true;
                break;
            }
        }

        accounts.set(accountIndex, bip32);
        if (hasUsage) {
            usedAccounts.add(accountIndex);
        }
        console.info(`Discovered account ${accountIndex} (hasUsage: ${hasUsage})`);

        if (!hasUsage && accountIndex > 0) {
            // Load this account but don't continue scanning further
            break;
        }

        accountIndex++;
    }

    return { accounts, usedAccounts };
}

async function discoverDemoAccounts() {
    const accounts = new Map<number, KaspaBIP32>();
    const xpub = getDemoXPub();
    accounts.set(0, new KaspaBIP32(xpub.compressedPublicKey, xpub.chainCode));
    return { accounts, usedAccounts: new Set<number>() };
}

async function createNewAccount(
    bip32Accounts,
    usedAccounts,
    availableAccounts,
    setBip32Accounts,
    setUsedAccounts,
    setAvailableAccounts,
    setAccount,
    setUserSettings,
    deviceType,
) {
    const highestAccount = Math.max(...availableAccounts.map((a) => Number(a.value)));
    const nextAccount = highestAccount + 1;

    if (!usedAccounts.has(highestAccount)) {
        notifications.show({
            title: 'New account not created when unused account exists',
            color: 'yellow',
            message: `Use Account ${highestAccount} first before creating a new account`,
            autoClose: false,
        });
        return;
    }

    if (deviceType !== 'demo') {
        const accountPath = `44'/111111'/${nextAccount}'`;
        const xpub = await getXPubFromLedger(accountPath);
        const bip32 = new KaspaBIP32(xpub.compressedPublicKey, xpub.chainCode);

        let hasUsage = false;
        for (let i = 0; i < 2; i++) {
            const addr = bip32.getAddress(0, i);
            const balanceData = await fetchAddressBalance(addr);
            if (Number(balanceData.balance) > 0) {
                hasUsage = true;
                break;
            }
        }

        const newAccounts = new Map(bip32Accounts);
        newAccounts.set(nextAccount, bip32);
        setBip32Accounts(newAccounts);

        if (hasUsage) {
            const newUsedAccounts = new Set(usedAccounts);
            newUsedAccounts.add(nextAccount);
            setUsedAccounts(newUsedAccounts);
        }

        const storageKey = sha256(bip32.rootNode.publicKey.toString('hex')).toString();
        const settings = new SettingsStore(storageKey, nextAccount);
        setUserSettings(settings);
    } else {
        const xpub = getDemoXPub();
        const bip32 = new KaspaBIP32(xpub.compressedPublicKey, xpub.chainCode);
        const newAccounts = new Map(bip32Accounts);
        newAccounts.set(nextAccount, bip32);
        setBip32Accounts(newAccounts);
    }

    setAvailableAccounts([
        ...availableAccounts,
        { value: String(nextAccount), label: `Account ${nextAccount}` },
    ]);
    setAccount(nextAccount);
}

function getDemoXPub() {
    const chainCode = Buffer.from([
        11, 165, 153, 169, 197, 186, 209, 16, 96, 101, 234, 180, 123, 72, 239, 160, 112, 244, 179,
        30, 150, 57, 201, 208, 150, 247, 117, 107, 36, 138, 111, 244,
    ]);
    const compressedPublicKey = Buffer.from([
        3, 90, 25, 171, 24, 66, 175, 67, 29, 59, 79, 168, 138, 21, 177, 254, 125, 124, 63, 110, 38,
        232, 8, 18, 74, 16, 220, 5, 35, 53, 45, 70, 45,
    ]);

    return {
        compressedPublicKey,
        chainCode,
    };
}

export default function Dashboard() {
    const [addresses, setAddresses] = useState<IAddressData[]>([]);
    const [rawAddresses, setRawAddresses] = useState<IAddressData[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<ISelectedAddress | null>(null);
    const [account, setAccount] = useState(0);
    const [availableAccounts, setAvailableAccounts] = useState<{ value: string; label: string }[]>([
        { value: '0', label: 'Account 0' },
    ]);
    const [activeTab, setActiveTab] = useState('addresses');
    const [isTransportInitialized, setTransportInitialized] = useState(false);
    const [bip32Accounts, setBip32Accounts] = useState<Map<number, KaspaBIP32>>(new Map());
    const [usedAccounts, setUsedAccounts] = useState<Set<number>>(new Set());
    const [userSettings, setUserSettings] = useState<SettingsStore>();
    const [enableGenerate, setEnableGenerate] = useState(false);
    const [mempoolEntryToReplace, setMempoolEntryToReplace] = useState<IMempoolEntry | null>(null);
    const [pendingTxId, setPendingTxId] = useState<string | null>(null);
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [isLoadingAccount, setIsLoadingAccount] = useState(false);

    const { ref: containerRef, width: containerWidth, height: containerHeight } = useElementSize();

    async function generateNewAddress() {
        setEnableGenerate(false);
        try {
            const newReceiveAddressIndex = userSettings.getSetting('lastReceiveIndex') + 1;

            const derivationPath = `44'/111111'/${account}'/0/${newReceiveAddressIndex}`;
            const { address } =
                deviceType === 'demo'
                    ? { address: bip32Accounts.get(account).getAddress(0, newReceiveAddressIndex) }
                    : await getAddress(derivationPath);
            const rawAddress: IAddressData = {
                key: address,
                derivationPath,
                address,
                addressType: 0,
                addressIndex: newReceiveAddressIndex,
                balance: 0,
                loading: true,
                utxos: [],
            };

            setRawAddresses([...rawAddresses, rawAddress]);
            setAddresses([...rawAddresses, rawAddress]);
            userSettings.setSetting('lastReceiveIndex', newReceiveAddressIndex);

            try {
                if (deviceType === 'demo') {
                    rawAddress.balance = Math.round(Math.random() * 10000);
                    await delay(Math.round(Math.random() * 3000)).then(() => {
                        rawAddress.loading = false;
                    });
                } else {
                    await loadAddressDetails(rawAddress);
                }

                setRawAddresses([...rawAddresses, rawAddress]);
                setAddresses([...rawAddresses, rawAddress]);
            } catch (e) {
                console.error(e);
                notifications.show({
                    title: 'Error',
                    message: 'Unable to load address details. Refresh the page to retry.',
                    autoClose: false,
                    color: 'red',
                });
            }
        } catch (e) {
            console.info(e);
            if (e instanceof LockedDeviceError) {
                notifications.show({
                    title: 'Error',
                    message: e.message,
                    autoClose: false,
                    color: 'red',
                });
            } else if (e.message) {
                notifications.show({
                    title: 'Error',
                    message: `Unable to generate new address: ${e.message}`,
                    autoClose: false,
                    color: 'red',
                });
            } else {
                console.error(e);
                notifications.show({
                    title: 'Error',
                    message: 'Unable to generate new address',
                    autoClose: false,
                    color: 'red',
                });
            }
        } finally {
            setEnableGenerate(true);
        }
    }

    const searchParams = new URLSearchParams(window.location.search);

    const deviceType = searchParams.get('deviceType');

    useEffect(() => {
        if (isTransportInitialized) {
            return () => {};
        }

        if (deviceType === 'demo') {
            setTransportInitialized(true);
            discoverDemoAccounts().then(({ accounts, usedAccounts }) => {
                setBip32Accounts(accounts);
                setUsedAccounts(usedAccounts);
            });
            return () => {};
        }

        let unloaded = false;

        if (!isLedgerTransportInitialized()) {
            setShowConnectModal(true);
        } else {
            initTransport(deviceType)
                .then(() => {
                    if (!unloaded) {
                        setTransportInitialized(true);

                        return discoverAccounts().then(({ accounts, usedAccounts }) => {
                            setBip32Accounts(accounts);
                            setUsedAccounts(usedAccounts);
                        });
                    }

                    return null;
                })
                .catch((e) => {
                    notifications.show({
                        title: 'Error',
                        color: 'red',
                        message:
                            'Please make sure your device is unlocked and the Kaspa app is open',
                        autoClose: false,
                    });
                    console.error(e);
                });
        }

        return () => {
            unloaded = true;
        };
    }, [isTransportInitialized, deviceType, bip32Accounts]);

    useEffect(() => {
        const bip32base = bip32Accounts.get(0);
        if (!bip32base) {
            return;
        }

        // We need to somehow differentiate between different devices
        // This gives us a unique key we can use
        const storageKey = sha256(bip32base.rootNode.publicKey.toString('hex')).toString();
        setUserSettings(new SettingsStore(storageKey, account));
    }, [bip32Accounts, deviceType, account]);

    useEffect(() => {
        const accounts = [{ value: '0', label: 'Account 0' }];
        let maxUsedAccount = -1;
        for (const acc of usedAccounts) {
            if (acc > maxUsedAccount) {
                maxUsedAccount = acc;
            }
        }
        for (let i = 1; i <= maxUsedAccount; i++) {
            accounts.push({ value: String(i), label: `Account ${i}` });
        }
        setAvailableAccounts(accounts);
    }, [usedAccounts]);

    useEffect(() => {
        if (!userSettings) {
            return;
        }

        const bip32base = bip32Accounts.get(account);
        if (!bip32base) {
            return;
        }

        // Show loading state immediately on account switch
        setIsLoadingAccount(true);
        setSelectedAddress(null);
        setActiveTab('addresses');
        setEnableGenerate(false);

        addressInitialized.delete(account);
        addressLoading.delete(account);
        setAddresses([]);
        setRawAddresses([]);

        if (deviceType === 'usb' || deviceType === 'bluetooth') {
            loadOrScanAddressBatch(
                bip32base,
                setAddresses,
                setRawAddresses,
                userSettings,
                account,
            ).finally(() => {
                setEnableGenerate(true);
                setIsLoadingAccount(false);
            });
        } else if (deviceType === 'demo') {
            userSettings.setSetting('lastReceiveIndex', 0);
            demoLoadAddress(
                bip32base,
                setAddresses,
                setRawAddresses,
                userSettings.getSetting('lastReceiveIndex'),
            ).finally(() => {
                setIsLoadingAccount(false);
            });
        }
    }, [bip32Accounts, userSettings, deviceType, account]);

    const breadcrumbs = [
        <Anchor key='home' href={'/'}>
            Home
        </Anchor>,
    ];

    return (
        <Stack className={styles.main}>
            <Header
                accountSelector={
                    <Group gap={4}>
                        <Select
                            data={availableAccounts}
                            value={String(account)}
                            onChange={(val) => setAccount(Number(val))}
                            size='xs'
                            w={120}
                        />
                        <Button
                            size='xs'
                            variant='default'
                            p={4}
                            onClick={() =>
                                createNewAccount(
                                    bip32Accounts,
                                    usedAccounts,
                                    availableAccounts,
                                    setBip32Accounts,
                                    setUsedAccounts,
                                    setAvailableAccounts,
                                    setAccount,
                                    setUserSettings,
                                    deviceType,
                                )
                            }
                        >
                            <IconPlus size={16} />
                        </Button>
                    </Group>
                }
            >
                <Breadcrumbs>{breadcrumbs}</Breadcrumbs>
            </Header>

            <Modal
                centered
                opened={showConnectModal}
                withCloseButton={false}
                onClose={() => setShowConnectModal(false)}
                title={'Connect Ledger Device via ' + deviceType}
            >
                <Stack>
                    <Text>Please connect your Ledger device and open the Kaspa app.</Text>
                    <Button
                        onClick={() => {
                            setShowConnectModal(false);
                            initTransport(deviceType)
                                .then(() => {
                                    setTransportInitialized(true);
                                    return discoverAccounts().then(({ accounts, usedAccounts }) => {
                                        setBip32Accounts(accounts);
                                        setUsedAccounts(usedAccounts);
                                    });
                                })
                                .catch((e) => {
                                    if (e.name === 'TransportOpenUserCancelled') {
                                        setShowConnectModal(true);
                                    } else {
                                        notifications.show({
                                            title: 'Error',
                                            color: 'red',
                                            message:
                                                'Please make sure your device is unlocked and the Kaspa app is open',
                                            autoClose: false,
                                        });
                                    }
                                    console.error(e);
                                });
                        }}
                    >
                        Connect Device
                    </Button>
                </Stack>
            </Modal>

            <Center>
                <Tabs
                    value={activeTab}
                    onChange={setActiveTab}
                    className={styles.tabs}
                    maw={1080}
                    ref={containerRef}
                >
                    <Tabs.List>
                        <Tabs.Tab value='addresses'>Addresses</Tabs.Tab>
                        <Tabs.Tab value='overview' disabled={!selectedAddress}>
                            Overview
                        </Tabs.Tab>
                        <Tabs.Tab value='transactions' disabled={!selectedAddress}>
                            Transactions
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value='addresses'>
                        {isLoadingAccount ? (
                            <Center p='xl'>
                                <Loader size='lg' />
                            </Center>
                        ) : (
                            <>
                                <AddressesTab
                                    addresses={addresses}
                                    selectedAddress={selectedAddress}
                                    setSelectedAddress={setSelectedAddress}
                                    setActiveTab={setActiveTab}
                                    containerWidth={containerWidth}
                                    containerHeight={containerHeight}
                                />

                                <Center>
                                    <Button onClick={generateNewAddress} disabled={!enableGenerate}>
                                        Generate New Address
                                    </Button>
                                </Center>
                            </>
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel value='overview'>
                        <OverviewTab
                            addresses={addresses}
                            selectedAddress={selectedAddress}
                            setSelectedAddress={setSelectedAddress}
                            setMempoolEntryToReplace={setMempoolEntryToReplace}
                            setAddresses={setAddresses}
                            containerWidth={containerWidth}
                            containerHeight={containerHeight}
                            deviceType={deviceType}
                            mempoolEntryToReplace={mempoolEntryToReplace}
                            setPendingTxId={setPendingTxId}
                            bip32={bip32Accounts.get(account)}
                            userSettings={userSettings}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value='transactions'>
                        <TransactionsTab
                            selectedAddress={selectedAddress}
                            containerWidth={containerWidth}
                            containerHeight={containerHeight}
                            pendingTxId={pendingTxId}
                            setMempoolEntryToReplace={setMempoolEntryToReplace}
                            setActiveTab={setActiveTab}
                        />
                    </Tabs.Panel>
                </Tabs>
            </Center>
        </Stack>
    );
}
